/**
 * ReassignTaskModal - 重新分配任务 Modal
 *
 * 设计理念 (2026-02-03):
 * - Reassign 本质是创建新任务，司机分配在 Dispatch 页面统一处理
 * - 上半部分：显示原任务路线信息
 * - 下半部分：显示新任务路线预览（交接地 → 目的地）
 * - 新任务创建后状态为 unassigned
 * - 目的地默认为旧 task 的目的地，但可以通过 Change 按钮修改
 *
 * 重新分配后：
 * - 原任务标记为 'uncompleted'，路线变为 origin → 交接地点
 * - 创建新任务（unassigned），路线为 交接地点 → 新目的地
 * - 工资计算：原司机按 origin → 交接地点 计算
 */

import React, { useState, useEffect, useMemo, useRef } from 'react'
import { createPortal } from 'react-dom'
import {
  X,
  GitBranch,
  MapPin,
  Warehouse,
  Ship,
  FileText,
  Truck,
  Clock,
  Building2,
  Briefcase,
  UserCircle,
  Plus,
  AlertCircle
} from 'lucide-react'

// Destination option interface (same as RerouteModal)
interface Destination {
  id: number
  type: 'warehouse' | 'yard' | 'port' | 'shipper' | 'client'
  name: string
  shortName?: string
  address?: string
  contact?: string
  phone?: string
  city?: string
  state?: string
}

export interface ReassignData {
  handoverLocation: string
  handoverLocationType?: string
  handoverLocationId?: number
  // New destination fields
  newDestinationType?: string
  newDestinationId?: number
  newDestinationName?: string
  newDestinationAddress?: string
  reason?: string
}

interface ReassignTaskModalProps {
  open: boolean
  onClose: () => void
  onConfirm: (data: ReassignData) => Promise<void>
  isSaving?: boolean
  taskId: string
  containerNumber?: string
  // 原路线信息
  originName?: string
  originType?: string
  destinationName?: string
  destinationType?: string
  destinationId?: number
  // 暂停信息
  pauseLocation?: string
  pauseReason?: string
  // 当前司机信息
  currentDriverName?: string
  // 目的地选项 (same as RerouteModal)
  destinations?: Destination[]
  /** Groups whose fetch failed — surfaced so an incomplete list cannot read as
   *  an empty one. Same contract as RerouteModal. */
  failedDestinationGroups?: string[]
  destinationsLoading?: boolean
  onRetryDestinations?: (groups: string[]) => void
}

// Icon mapping for destination types
const TYPE_ICONS: Record<string, React.ElementType> = {
  warehouse: Warehouse,
  yard: Building2,
  port: Ship,
  shipper: Briefcase,
  client: UserCircle,
  custom: MapPin
}

const TYPE_COLORS: Record<string, string> = {
  warehouse: 'text-orange-500',
  yard: 'text-purple-500',
  port: 'text-blue-500',
  shipper: 'text-green-500',
  client: 'text-teal-500',
  custom: 'text-gray-500'
}

const TYPE_BG_COLORS: Record<string, string> = {
  warehouse: 'bg-orange-50 border-orange-200',
  yard: 'bg-purple-50 border-purple-200',
  port: 'bg-blue-50 border-blue-200',
  shipper: 'bg-green-50 border-green-200',
  client: 'bg-teal-50 border-teal-200',
  custom: 'bg-gray-50 border-gray-200'
}

export const ReassignTaskModal: React.FC<ReassignTaskModalProps> = ({
  open,
  onClose,
  onConfirm,
  isSaving = false,
  taskId,
  containerNumber,
  originName,
  originType,
  destinationName,
  destinationType,
  destinationId,
  pauseLocation,
  pauseReason,
  currentDriverName,
  destinations = [],
  failedDestinationGroups = [],
  destinationsLoading = false,
  onRetryDestinations
}) => {
  const [handoverLocation, setHandoverLocation] = useState('')
  const [reason, setReason] = useState('')

  // New destination state (defaults to original destination)
  const [newDestination, setNewDestination] = useState<{
    type: string
    id?: number
    name: string
    address?: string
  } | null>(null)

  // Destination selector state
  const [isChangingDestination, setIsChangingDestination] = useState(false)
  const [searchValue, setSearchValue] = useState('')
  const dropdownRef = useRef<HTMLDivElement>(null)

  // Custom address state
  const [showCustomAddressForm, setShowCustomAddressForm] = useState(false)
  const [customAddressName, setCustomAddressName] = useState('')
  const [customAddressValue, setCustomAddressValue] = useState('')

  // Reset form when modal opens
  useEffect(() => {
    if (open) {
      // Pre-fill with pause location if available
      setHandoverLocation(pauseLocation || '')
      setReason(pauseReason || '')
      // Reset destination to original
      setNewDestination(null)
      setIsChangingDestination(false)
      setSearchValue('')
      setShowCustomAddressForm(false)
      setCustomAddressName('')
      setCustomAddressValue('')
    }
  }, [open, pauseLocation, pauseReason])

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsChangingDestination(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  // Group destinations by type
  const groupedDestinations = useMemo(() => {
    // Rows show "Everport Terminal Services (ETS)", so the short name has to be
    // searchable too — same picker, same rule as Reroute.
    const search = searchValue.trim().toLowerCase()
    const filtered = destinations.filter(d => {
      if (!search) return true
      return (
        d.name.toLowerCase().includes(search) ||
        (d.shortName?.toLowerCase().includes(search)) ||
        (d.address?.toLowerCase().includes(search)) ||
        (d.city?.toLowerCase().includes(search))
      )
    })

    return {
      warehouses: filtered.filter(d => d.type === 'warehouse'),
      yards: filtered.filter(d => d.type === 'yard'),
      ports: filtered.filter(d => d.type === 'port'),
      shippers: filtered.filter(d => d.type === 'shipper'),
      clients: filtered.filter(d => d.type === 'client')
    }
  }, [destinations, searchValue])

  // Handle selecting a destination from dropdown
  const handleSelectDestination = (dest: Destination) => {
    setNewDestination({
      type: dest.type,
      id: dest.id,
      name: dest.name,
      address: dest.address
    })
    setIsChangingDestination(false)
    setSearchValue('')
  }

  // Handle custom address selection
  const handleSelectCustomAddress = () => {
    setIsChangingDestination(false)
    setShowCustomAddressForm(true)
    setCustomAddressName('')
    setCustomAddressValue('')
  }

  // Handle custom address form submit
  const handleCustomAddressSubmit = () => {
    if (!customAddressValue.trim()) return

    setNewDestination({
      type: 'custom',
      name: customAddressName.trim() || 'Custom Location',
      address: customAddressValue.trim()
    })
    setShowCustomAddressForm(false)
    setCustomAddressName('')
    setCustomAddressValue('')
  }

  // Cancel custom address form
  const handleCustomAddressCancel = () => {
    setShowCustomAddressForm(false)
    setCustomAddressName('')
    setCustomAddressValue('')
  }

  // Get the current destination to display
  const currentDestination = newDestination || {
    type: destinationType || 'warehouse',
    id: destinationId,
    name: destinationName || 'Destination'
  }

  const handleConfirm = async () => {
    if (!handoverLocation.trim() || !reason.trim()) {
      return
    }
    await onConfirm({
      handoverLocation: handoverLocation.trim(),
      handoverLocationType: 'custom',
      // Include new destination info if changed
      newDestinationType: newDestination?.type || destinationType,
      newDestinationId: newDestination?.id || destinationId,
      newDestinationName: newDestination?.name || destinationName,
      newDestinationAddress: newDestination?.address,
      reason: reason.trim()
    })
  }

  // 获取位置类型图标
  const getLocationIcon = (type?: string) => {
    switch (type) {
      case 'port':
        return <Ship className="w-4 h-4 text-blue-500" />
      case 'warehouse':
        return <Warehouse className="w-4 h-4 text-amber-500" />
      case 'yard':
        return <Truck className="w-4 h-4 text-green-500" />
      default:
        return <MapPin className="w-4 h-4 text-gray-500" />
    }
  }

  if (!open) return null

  // z-[100]：必须高过详情弹窗的 z-[70]，否则被它的黑底盖住＝点了没反应
  return createPortal(
    <div className="fixed inset-0 z-[100] flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg mx-4 overflow-hidden">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between bg-gradient-to-r from-blue-50 to-indigo-50">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <GitBranch className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Reassign Task</h3>
              <p className="text-sm text-gray-500">
                {containerNumber ? `Container: ${containerNumber}` : `Task: ${taskId}`}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-5 space-y-5 max-h-[calc(100vh-260px)] overflow-y-auto">
          {/* Original Route — timeline style, no border box */}
          <div>
            <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider mb-3">Original Route</p>
            <div className="flex gap-3">
              {/* Timeline dots + line */}
              <div className="flex flex-col items-center pt-0.5">
                <div className="w-2.5 h-2.5 rounded-full bg-blue-400 ring-[3px] ring-blue-100" />
                <div className="w-px flex-1 bg-gray-200 my-1" />
                <div className="w-2.5 h-2.5 rounded-full bg-gray-400 ring-[3px] ring-gray-100" />
              </div>
              {/* Content */}
              <div className="flex-1 min-w-0 -mt-0.5">
                <div className="flex items-center gap-2 mb-4">
                  {getLocationIcon(originType)}
                  <span className="text-sm font-medium text-gray-800 truncate">{originName || 'Origin'}</span>
                </div>
                <div className="flex items-center gap-2">
                  {getLocationIcon(destinationType)}
                  <span className="text-sm font-medium text-gray-800 truncate">{destinationName || 'Destination'}</span>
                </div>
              </div>
            </div>
            {currentDriverName && (
              <div className="mt-2.5 ml-6 flex items-center gap-1.5 text-xs text-gray-400">
                <Clock className="w-3 h-3" />
                <span>Driver <span className="text-gray-600 font-medium">{currentDriverName}</span> completing at handover</span>
              </div>
            )}
          </div>

          {/* Divider */}
          <div className="border-t border-dashed border-gray-200" />

          {/* Handover Location Input */}
          <div className="space-y-1.5">
            <label className="flex items-center gap-1.5 text-sm font-medium text-gray-700">
              <MapPin className="w-3.5 h-3.5 text-red-400" />
              Handover Location
              <span className="text-red-400 text-xs">*</span>
            </label>
            <input
              value={handoverLocation}
              onChange={(e) => setHandoverLocation(e.target.value)}
              placeholder="Enter handover location address..."
              className={`w-full px-3 py-2 text-sm rounded-lg bg-gray-50 border transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400 focus:bg-white ${
                !handoverLocation.trim() ? 'border-gray-200' : 'border-gray-200'
              }`}
            />
            <p className="text-[11px] text-gray-400 ml-0.5">Where the current driver will hand over the container</p>
          </div>

          {/* New Task Route Preview — timeline style */}
          <div>
            <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider mb-3">New Task Route</p>
            <div className="flex gap-3">
              {/* Timeline dots + line */}
              <div className="flex flex-col items-center pt-0.5">
                <div className="w-2.5 h-2.5 rounded-full bg-red-400 ring-[3px] ring-red-100" />
                <div className="w-px flex-1 bg-blue-200 my-1" />
                <div className={`w-2.5 h-2.5 rounded-full ring-[3px] ${
                  currentDestination.type === 'warehouse' ? 'bg-orange-400 ring-orange-100'
                  : currentDestination.type === 'port' ? 'bg-blue-400 ring-blue-100'
                  : currentDestination.type === 'yard' ? 'bg-purple-400 ring-purple-100'
                  : 'bg-green-400 ring-green-100'
                }`} />
              </div>
              {/* Content */}
              <div className="flex-1 min-w-0 -mt-0.5">
                {/* Origin: handover location */}
                <div className="mb-4">
                  <span className={`text-sm font-medium ${handoverLocation.trim() ? 'text-gray-800' : 'text-gray-300 italic'}`}>
                    {handoverLocation.trim() || 'Enter handover location'}
                  </span>
                </div>

                {/* Destination (changeable) */}
                <div className="relative" ref={dropdownRef}>
                  <div className="flex items-center gap-2">
                    {(() => {
                      const Icon = TYPE_ICONS[currentDestination.type] || MapPin
                      const iconColor = TYPE_COLORS[currentDestination.type] || 'text-green-500'
                      return <Icon className={`w-3.5 h-3.5 ${iconColor} flex-shrink-0`} />
                    })()}
                    <span className="text-sm font-medium text-gray-800 truncate">{currentDestination.name}</span>
                    {newDestination?.address && (
                      <span className="text-xs text-gray-400 truncate hidden sm:inline">{newDestination.address}</span>
                    )}
                    {destinations.length > 0 && (
                      <button
                        onClick={() => { setIsChangingDestination(!isChangingDestination); setSearchValue('') }}
                        className="ml-auto px-2 py-0.5 text-[11px] font-medium text-blue-500 hover:text-blue-700 hover:bg-blue-50 rounded transition-colors flex-shrink-0"
                      >
                        Change
                      </button>
                    )}
                  </div>

                  {/* Destination Dropdown */}
                  {isChangingDestination && (
                    <div className="absolute z-20 w-80 mt-2 bg-white rounded-xl shadow-lg shadow-gray-200/60 border border-gray-100 overflow-hidden">
                      {/* Search */}
                      <div className="p-2.5 bg-gray-50/80">
                        <div className="relative">
                          <input
                            type="text"
                            value={searchValue}
                            onChange={(e) => setSearchValue(e.target.value)}
                            placeholder="Search destinations..."
                            className="w-full pl-8 pr-3 py-1.5 bg-white border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400 transition-colors"
                            autoFocus
                          />
                          <MapPin className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
                        </div>
                      </div>

                      {/* Scrollable list */}
                      <div className="max-h-52 overflow-y-auto">
                        {/* A group that failed to load says so. Without this an
                            incomplete list is indistinguishable from a company
                            that owns none of that kind of place. */}
                        {failedDestinationGroups.length > 0 && (
                          <div className="px-3 py-2 bg-red-50/60 flex items-center gap-2">
                            <AlertCircle className="w-3.5 h-3.5 text-red-500 flex-shrink-0" />
                            <div className="flex-1 min-w-0">
                              <p className="text-[11px] font-medium text-red-700 truncate">
                                Couldn&apos;t load: {failedDestinationGroups.join(', ')}
                              </p>
                              <p className="text-[10px] text-red-500">This list is incomplete, not empty.</p>
                            </div>
                            <button
                              onClick={() => onRetryDestinations?.(failedDestinationGroups)}
                              disabled={destinationsLoading}
                              className="px-2 py-0.5 text-[11px] font-medium text-red-700 bg-red-100 hover:bg-red-200 rounded transition-colors disabled:opacity-50 flex-shrink-0"
                            >
                              {destinationsLoading ? 'Retrying…' : 'Retry'}
                            </button>
                          </div>
                        )}

                        {/* Warehouses */}
                        {groupedDestinations.warehouses.length > 0 && (
                          <div>
                            <div className="px-3 py-1 text-[10px] font-bold text-orange-500 uppercase tracking-wider bg-orange-50/50">Warehouses</div>
                            {groupedDestinations.warehouses.map((dest) => (
                              <button key={`warehouse-${dest.id}`} onClick={() => handleSelectDestination(dest)}
                                className="w-full px-3 py-1.5 text-left hover:bg-gray-50 flex items-center gap-2 transition-colors">
                                <Warehouse className="w-3.5 h-3.5 text-orange-400 flex-shrink-0" />
                                <div className="flex-1 min-w-0">
                                  <p className="text-sm text-gray-800 truncate">{dest.name}{dest.shortName && dest.shortName !== dest.name ? ` (${dest.shortName})` : ''}</p>
                                  {dest.address && <p className="text-[11px] text-gray-400 truncate">{dest.address}</p>}
                                </div>
                              </button>
                            ))}
                          </div>
                        )}

                        {/* Yards */}
                        {groupedDestinations.yards.length > 0 && (
                          <div>
                            <div className="px-3 py-1 text-[10px] font-bold text-purple-500 uppercase tracking-wider bg-purple-50/50">Yards</div>
                            {groupedDestinations.yards.map((dest) => (
                              <button key={`yard-${dest.id}`} onClick={() => handleSelectDestination(dest)}
                                className="w-full px-3 py-1.5 text-left hover:bg-gray-50 flex items-center gap-2 transition-colors">
                                <Building2 className="w-3.5 h-3.5 text-purple-400 flex-shrink-0" />
                                <div className="flex-1 min-w-0">
                                  <p className="text-sm text-gray-800 truncate">{dest.name}{dest.shortName && dest.shortName !== dest.name ? ` (${dest.shortName})` : ''}</p>
                                  {dest.address && <p className="text-[11px] text-gray-400 truncate">{dest.address}</p>}
                                </div>
                              </button>
                            ))}
                          </div>
                        )}

                        {/* Ports */}
                        {groupedDestinations.ports.length > 0 && (
                          <div>
                            <div className="px-3 py-1 text-[10px] font-bold text-blue-500 uppercase tracking-wider bg-blue-50/50">Ports</div>
                            {groupedDestinations.ports.map((dest) => (
                              <button key={`port-${dest.id}`} onClick={() => handleSelectDestination(dest)}
                                className="w-full px-3 py-1.5 text-left hover:bg-gray-50 flex items-center gap-2 transition-colors">
                                <Ship className="w-3.5 h-3.5 text-blue-400 flex-shrink-0" />
                                <div className="flex-1 min-w-0">
                                  <p className="text-sm text-gray-800 truncate">{dest.name}{dest.shortName && dest.shortName !== dest.name ? ` (${dest.shortName})` : ''}</p>
                                  {dest.address && <p className="text-[11px] text-gray-400 truncate">{dest.address}</p>}
                                </div>
                              </button>
                            ))}
                          </div>
                        )}

                        {/* Shippers */}
                        {groupedDestinations.shippers.length > 0 && (
                          <div>
                            <div className="px-3 py-1 text-[10px] font-bold text-green-500 uppercase tracking-wider bg-green-50/50">Shippers</div>
                            {groupedDestinations.shippers.map((dest) => (
                              <button key={`shipper-${dest.id}`} onClick={() => handleSelectDestination(dest)}
                                className="w-full px-3 py-1.5 text-left hover:bg-gray-50 flex items-center gap-2 transition-colors">
                                <Briefcase className="w-3.5 h-3.5 text-green-400 flex-shrink-0" />
                                <div className="flex-1 min-w-0">
                                  <p className="text-sm text-gray-800 truncate">{dest.name}{dest.shortName && dest.shortName !== dest.name ? ` (${dest.shortName})` : ''}</p>
                                  {dest.address && <p className="text-[11px] text-gray-400 truncate">{dest.address}</p>}
                                </div>
                              </button>
                            ))}
                          </div>
                        )}

                        {/* Clients */}
                        {groupedDestinations.clients.length > 0 && (
                          <div>
                            <div className="px-3 py-1 text-[10px] font-bold text-teal-500 uppercase tracking-wider bg-teal-50/50">Clients</div>
                            {groupedDestinations.clients.map((dest) => (
                              <button key={`client-${dest.id}`} onClick={() => handleSelectDestination(dest)}
                                className="w-full px-3 py-1.5 text-left hover:bg-gray-50 flex items-center gap-2 transition-colors">
                                <UserCircle className="w-3.5 h-3.5 text-teal-400 flex-shrink-0" />
                                <div className="flex-1 min-w-0">
                                  <p className="text-sm text-gray-800 truncate">{dest.name}{dest.shortName && dest.shortName !== dest.name ? ` (${dest.shortName})` : ''}</p>
                                  {dest.address && <p className="text-[11px] text-gray-400 truncate">{dest.address}</p>}
                                </div>
                              </button>
                            ))}
                          </div>
                        )}

                        {/* No results */}
                        {groupedDestinations.warehouses.length === 0 &&
                         groupedDestinations.yards.length === 0 &&
                         groupedDestinations.ports.length === 0 &&
                         groupedDestinations.shippers.length === 0 &&
                         groupedDestinations.clients.length === 0 &&
                         failedDestinationGroups.length === 0 &&
                         !destinationsLoading &&
                         searchValue.trim() !== '' && (
                          <div className="px-4 py-5 text-center">
                            <p className="text-sm text-gray-400">No results for &ldquo;{searchValue}&rdquo;</p>
                          </div>
                        )}
                      </div>

                      {/* Custom Address Option */}
                      <div className="border-t border-gray-100">
                        <button
                          onClick={handleSelectCustomAddress}
                          className="w-full px-3 py-2 text-left hover:bg-blue-50/50 flex items-center gap-2 transition-colors"
                        >
                          <Plus className="w-3.5 h-3.5 text-blue-500" />
                          <span className="text-sm text-blue-500 font-medium">Custom Address</span>
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
            {/* Unassigned badge */}
            <div className="mt-2.5 ml-6 flex items-center gap-1.5">
              <span className="px-1.5 py-0.5 bg-amber-50 text-amber-600 text-[10px] font-semibold rounded tracking-wide uppercase">Unassigned</span>
              <span className="text-[11px] text-gray-400">Assign driver in Dispatch Console</span>
            </div>
          </div>

          {/* Custom Address Form */}
          {showCustomAddressForm && (
            <div className="rounded-lg bg-gray-50 p-4 space-y-3">
              <div className="flex items-center justify-between">
                <p className="text-xs font-semibold text-gray-500 flex items-center gap-1.5">
                  <MapPin className="w-3.5 h-3.5 text-blue-400" />
                  Custom Destination
                </p>
                <button onClick={handleCustomAddressCancel} className="text-gray-400 hover:text-gray-600 p-0.5 rounded hover:bg-gray-200 transition-colors">
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
              <input
                type="text"
                value={customAddressName}
                onChange={(e) => setCustomAddressName(e.target.value)}
                placeholder="Location name (optional)"
                className="w-full px-3 py-1.5 bg-white border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400 transition-colors"
              />
              <textarea
                value={customAddressValue}
                onChange={(e) => setCustomAddressValue(e.target.value)}
                placeholder="Street, City, State ZIP *"
                rows={2}
                className="w-full px-3 py-1.5 bg-white border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400 resize-none transition-colors"
                autoFocus
              />
              <div className="flex justify-end gap-2">
                <button onClick={handleCustomAddressCancel} className="px-3 py-1 text-xs text-gray-500 hover:text-gray-700 rounded-md hover:bg-gray-200 transition-colors">Cancel</button>
                <button
                  onClick={handleCustomAddressSubmit}
                  disabled={!customAddressValue.trim()}
                  className={`px-3 py-1 text-xs font-medium rounded-md transition-all ${
                    customAddressValue.trim() ? 'bg-blue-500 hover:bg-blue-600 text-white' : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                  }`}
                >Set Destination</button>
              </div>
            </div>
          )}

          {/* Divider */}
          <div className="border-t border-dashed border-gray-200" />

          {/* Reason */}
          <div className="space-y-1.5">
            <label className="flex items-center gap-1.5 text-sm font-medium text-gray-700">
              <FileText className="w-3.5 h-3.5 text-gray-400" />
              Reason
              <span className="text-red-400 text-xs">*</span>
            </label>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g., Driver unavailable, Vehicle breakdown, Emergency..."
              rows={2}
              className="w-full px-3 py-2 text-sm rounded-lg bg-gray-50 border border-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400 focus:bg-white resize-none transition-colors"
            />
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-3.5 border-t border-gray-100 bg-gray-50/80 flex items-center justify-between">
          <p className="text-[11px] text-gray-400">
            Original task marked as <span className="font-medium text-gray-500">uncompleted</span>
          </p>
          <div className="flex items-center gap-2">
            <button
              onClick={onClose}
              disabled={isSaving}
              className="px-4 py-1.5 text-sm font-medium text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleConfirm}
              disabled={isSaving || !handoverLocation.trim() || !reason.trim()}
              className={`px-4 py-1.5 text-sm font-medium rounded-lg transition-all ${
                isSaving || !handoverLocation.trim() || !reason.trim()
                  ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                  : 'bg-blue-500 hover:bg-blue-600 text-white shadow-sm shadow-blue-500/20'
              }`}
            >
              {isSaving ? 'Reassigning...' : 'Reassign'}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  )
}
